import { GameItem } from 'dt-common/generated/prisma/client';
import { ApiErrorMessage } from 'dt-common/types/ApiErrorMessages';
import mutationLootCavernsTile from '~/graphql/caverns/mutationLootCavernsTile';
import { GraphQlRequestError } from '~/graphql/graphqlClient';
import { logError } from '~/logger';
import Tools from '~/Tools';
import messages, { CavernsStoreMessage } from '../messages';

// Keep a private, module-scoped queue of tile IDs so we can batch rapid loot actions
// into a single mutation call (debounced).
const pendingTileIds = new Set<string>();
let pendingPlayerId: string | null = null;
let pendingGameSubmode: string | null = null;
let pendingContext: {
  setLootPickedUp: (data: { gold: number; pd: number; items: GameItem[]; }) => void;
  _worker: Worker;
  emit: (message: CavernsStoreMessage, data?: any) => void;
  onMissingCavernsRunData: () => void;
} | null = null;
let flushInFlight = false;

const flushPendingLoot = async () => {
  if (flushInFlight) return;
  if (!pendingPlayerId || !pendingGameSubmode || !pendingContext) return;
  if (pendingTileIds.size === 0) return;

  flushInFlight = true;

  const tile_ids = Array.from(pendingTileIds);
  pendingTileIds.clear();

  try {
    const data = await mutationLootCavernsTile({
      playerId: pendingPlayerId,
      game_submode: pendingGameSubmode,
      tile_ids,
    });

    const { gold, pd, items, tile_looted_ids } = data;
    pendingContext.setLootPickedUp({ gold, pd, items });

    if (tile_looted_ids?.length) {
      pendingContext._worker.postMessage({
        message_name: 'tile_looted',
        message_data: { tile_looted_ids },
      });
    }

    pendingContext.emit(messages.TOTAL_LOOT_PICKED_UP, data);
  } catch (err) {
    if (err instanceof GraphQlRequestError && err.errors.find((error) => error.message === ApiErrorMessage.NO_CURRENT_RUN_FOUND)) {
      pendingContext?.onMissingCavernsRunData();
      return;
    }
    // If the call fails, re-queue the IDs so the next loot action can retry.
    for (const id of tile_ids) pendingTileIds.add(id);
    logError(err, {
      module: 'CavernsStore',
      func: 'lootCavernsTile/flushPendingLoot',
      playerId: pendingPlayerId,
      game_submode: pendingGameSubmode,
      tile_ids: tile_ids.join(','),
    });
  } finally {
    flushInFlight = false;
    // If new tiles were queued while we were in-flight, schedule another flush.
    if (pendingTileIds.size > 0) {
      debouncedFlushPendingLoot(undefined as any);
    }
  }
};

const debouncedFlushPendingLoot = Tools.debounce(() => {
  void flushPendingLoot();
}, 700);

export default async function lootCavernsTile(
  {
    playerId,
    setLootPickedUp,
    _worker,
    emit,
    onMissingCavernsRunData,
  }: {
    playerId: string | null;
    setLootPickedUp: (data: { gold: number, pd: number, items: GameItem[]; }) => void;
    _worker: Worker;
    emit: (message: CavernsStoreMessage, data?: any) => void;
    onMissingCavernsRunData: () => void;
  },
  { game_submode, tile_id }: { game_submode: string, tile_id: string; }
) {
  if (!playerId) {
    throw new Error('Player ID is required');
  }

  try {
    pendingPlayerId = playerId;
    pendingGameSubmode = game_submode;
    pendingContext = { setLootPickedUp, _worker, emit, onMissingCavernsRunData };
    pendingTileIds.add(tile_id);

    // Debounce for 700ms to batch rapid tile-loot actions into a single mutation call.
    debouncedFlushPendingLoot(undefined as any);
  } catch (err) {
    logError(err, {
      module: 'CavernsStore',
      func: 'lootCavernsTile',
      playerId,
      game_submode,
      tile_id
    });
  }
}
